import React, { useState, useEffect } from 'react';
import Button from '../../ui/Button';
import { Search, X } from 'lucide-react';

const InvoiceFilters = ({ filters, setFilters, onClose, isRecurring = false }) => {
  const [localFilters, setLocalFilters] = useState({
    receiverName: '',
    invoiceNumber: '',
    receiverVatNumber: '',
    receiverIdNumber: '',
    totalAmountStart: '',
    totalAmountEnd: '',
    totalAmountBeforeTaxStart: '',
    totalAmountBeforeTaxEnd: '',
    creationDateStart: '',
    creationDateEnd: '',
    dueDateStart: '',
    dueDateEnd: '',
  });

  useEffect(() => {
    if (filters) {
      setLocalFilters(prev => ({ ...prev, ...filters }));
    }
  }, [filters]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApply = () => {
    // Only pass non-empty values
    const cleanedFilters = Object.entries(localFilters).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
    setFilters(cleanedFilters);
  };

  const handleClear = () => {
    const emptyFilters = {
      receiverName: '',
      invoiceNumber: '',
      receiverVatNumber: '',
      receiverIdNumber: '',
      totalAmountStart: '',
      totalAmountEnd: '',
      totalAmountBeforeTaxStart: '',
      totalAmountBeforeTaxEnd: '',
      creationDateStart: '',
      creationDateEnd: '',
      dueDateStart: '',
      dueDateEnd: '',
    };
    setLocalFilters(emptyFilters);
    setFilters({});
  };

  return (
    <div className="bg-gray-50 border-b border-gray-100 p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Филтри</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
          <X size={18} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Text Filters */}
        <div className="space-y-1">
          <label className="text-[13px] font-medium text-gray-600">Име на получател</label>
          <input type="text" name="receiverName" value={localFilters.receiverName} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" placeholder="Търсене по име..." />
        </div>
        {!isRecurring && (
          <div className="space-y-1">
            <label className="text-[13px] font-medium text-gray-600">Номер на фактура</label>
            <input type="number" name="invoiceNumber" value={localFilters.invoiceNumber} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" placeholder="Напр. 1004" />
          </div>
        )}
        <div className="space-y-1">
          <label className="text-[13px] font-medium text-gray-600">ЕИК на получател</label>
          <input type="text" name="receiverVatNumber" value={localFilters.receiverVatNumber} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" placeholder="ЕИК / Булстат" />
        </div>
        <div className="space-y-1">
          <label className="text-[13px] font-medium text-gray-600">ИН по ЗДДС</label>
          <input type="text" name="receiverIdNumber" value={localFilters.receiverIdNumber} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" placeholder="BG..." />
        </div>

        {/* Date Filters */}
        <div className="space-y-1">
          <label className="text-[13px] font-medium text-gray-600">{isRecurring ? 'Следваща дата на издаване (От)' : 'Дата на издаване (От)'}</label>
          <input type="date" name="creationDateStart" value={localFilters.creationDateStart} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" />
        </div>
        <div className="space-y-1">
          <label className="text-[13px] font-medium text-gray-600">{isRecurring ? 'Следваща дата на издаване (До)' : 'Дата на издаване (До)'}</label>
          <input type="date" name="creationDateEnd" value={localFilters.creationDateEnd} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" />
        </div>
        {!isRecurring && (
          <>
            <div className="space-y-1">
              <label className="text-[13px] font-medium text-gray-600">Падеж (От)</label>
              <input type="date" name="dueDateStart" value={localFilters.dueDateStart} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" />
            </div>
            <div className="space-y-1">
              <label className="text-[13px] font-medium text-gray-600">Падеж (До)</label>
              <input type="date" name="dueDateEnd" value={localFilters.dueDateEnd} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" />
            </div>
          </>
        )}

        {/* Amount Filters */}
        <div className="space-y-1">
          <label className="text-[13px] font-medium text-gray-600">Обща сума (От)</label>
          <input type="number" step="0.01" name="totalAmountStart" value={localFilters.totalAmountStart} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" placeholder="0.00" />
        </div>
        <div className="space-y-1">
          <label className="text-[13px] font-medium text-gray-600">Обща сума (До)</label>
          <input type="number" step="0.01" name="totalAmountEnd" value={localFilters.totalAmountEnd} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" placeholder="0.00" />
        </div>
        <div className="space-y-1">
          <label className="text-[13px] font-medium text-gray-600">Сума без ДДС (От)</label>
          <input type="number" step="0.01" name="totalAmountBeforeTaxStart" value={localFilters.totalAmountBeforeTaxStart} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" placeholder="0.00" />
        </div>
        <div className="space-y-1">
          <label className="text-[13px] font-medium text-gray-600">Сума без ДДС (До)</label>
          <input type="number" step="0.01" name="totalAmountBeforeTaxEnd" value={localFilters.totalAmountBeforeTaxEnd} onChange={handleChange} className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white" placeholder="0.00" />
        </div>
      </div>

      <div className="flex gap-3 justify-end border-t border-gray-100 pt-4">
        <Button variant="secondary" onClick={handleClear} className="text-[13px] px-4">
          Изчисти
        </Button>
        <Button variant="primary" onClick={handleApply} className="text-[13px] px-6 flex items-center gap-2">
          <Search size={16} />
          Приложи филтрите
        </Button>
      </div>
    </div>
  );
};

export default InvoiceFilters;
